import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/ui/tooltip';
import { cn } from '@lib/utils';
import { HelpCircleIcon } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';
import { formatOrganizationShortId, getAdminOrganizationHref } from '../../organizations/organization-hierarchy.utils';

type OrganizationAccessResolvedOption = {
  deleted?: boolean;
  disabledTooltip?: string;
  label: string;
};

type AdminUsersModalOrganizationAccessFieldProps = {
  editField?: React.ReactNode;
  emptyText: string;
  isEditing: boolean;
  maxItems: number;
  organizationIds: string[];
  resolveOrganizationOption: (organizationId: string) => OrganizationAccessResolvedOption | null;
  title: string;
  tooltip?: string;
};

export const AdminUsersModalOrganizationAccessField: React.FC<AdminUsersModalOrganizationAccessFieldProps> = (
  props,
) => {
  const { editField, emptyText, isEditing, maxItems, organizationIds, resolveOrganizationOption, title, tooltip } =
    props;
  const resolvedOrganizationRows = organizationIds
    .map((organizationId) => {
      const organizationOption = resolveOrganizationOption(organizationId);
      if (!organizationOption) {
        return null;
      }

      return {
        organizationId,
        organizationOption,
      };
    })
    .filter(
      (
        organizationRow,
      ): organizationRow is { organizationId: string; organizationOption: OrganizationAccessResolvedOption } =>
        organizationRow !== null,
    );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <span className="text-xs font-medium text-gray-500">{title}</span>
          {tooltip && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircleIcon className="size-3.5 cursor-help text-gray-400" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs font-normal text-xs">
                  <p>{tooltip}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {isEditing && (
          <span className="text-xs font-medium text-gray-500">
            ({organizationIds.length}/{maxItems})
          </span>
        )}
      </div>
      {isEditing ? (
        editField
      ) : resolvedOrganizationRows.length > 0 ? (
        <div className="max-h-64 overflow-y-auto rounded-md border border-gray-200">
          <div className="flex flex-col divide-y divide-gray-100">
            {resolvedOrganizationRows.map(({ organizationId, organizationOption }) => {
              const organizationLabel = (
                <div className="flex min-w-0 flex-col">
                  {organizationOption.deleted ? (
                    <span className="truncate text-sm text-gray-500">{organizationOption.label}</span>
                  ) : (
                    <Link
                      className="truncate text-sm text-blue-700 hover:underline"
                      to={getAdminOrganizationHref(organizationId)}
                    >
                      {organizationOption.label}
                    </Link>
                  )}
                  <span className="font-mono text-xs text-gray-500">{formatOrganizationShortId(organizationId)}</span>
                </div>
              );

              const content = (
                <div
                  className={cn(
                    'flex items-center justify-between gap-2 px-3 py-2',
                    organizationOption.deleted && 'bg-gray-50',
                  )}
                >
                  {organizationLabel}
                </div>
              );

              if (organizationOption.deleted && organizationOption.disabledTooltip) {
                return (
                  <TooltipProvider
                    key={`${title}-${organizationId}`}
                    delayDuration={0}
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>{content}</div>
                      </TooltipTrigger>
                      <TooltipContent className="font-normal text-xs">
                        <p>{organizationOption.disabledTooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              }

              return <div key={`${title}-${organizationId}`}>{content}</div>;
            })}
          </div>
        </div>
      ) : (
        <span className="text-sm text-gray-500">{emptyText}</span>
      )}
    </div>
  );
};
